import { APP_INITIALIZER, ErrorHandler, NgModule, provideZoneChangeDetection } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { APP_CONFIGURATION, AppConfiguration } from "@ramudden/core/constants";
import { environment } from "../environments/environment";
import { AppRoutingModule } from "./app-routing.module";
import { CookieNotificationComponent } from "./components/shared/cookie-notification/cookie-notification.component";
import { SigncoErrorHandler } from "./error-handler";
import { AdminGuard } from "./guards/admin.guard";
import { AuthGuard } from "./guards/auth.guard";
import { AuthorizationGuard } from "./guards/authorization.guard";
import { PendingChangesGuard } from "./guards/pending-changes.guard";
import { AuthInterceptor } from "./interceptors/auth.interceptor";
import { DefaultInterceptor } from "./interceptors/default.interceptor";
import { DomainDataService } from "./services/domain-data.service";

import { HTTP_INTERCEPTORS, HttpClient } from "@angular/common/http";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";

import { ConfigurationService } from "@ramudden/services";

import { JsonInterceptor } from "@ramudden/interceptors";

import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { Angulartics2Module } from "angulartics2";
import { ToastrModule } from "ngx-toastr";

import { SharedModule } from "./shared/shared.module";

import { UntypedFormBuilder } from "@angular/forms";
import { ServiceWorkerModule } from "@angular/service-worker";
import { MSAL_INSTANCE, MsalBroadcastService, MsalRedirectComponent, MsalService } from "@azure/msal-angular";
import { BrowserCacheLocation, IPublicClientApplication, LogLevel, PublicClientApplication } from "@azure/msal-browser";
// @ts-ignore - Primeng forget there index.ts I guess
import Lara from "@primeuix/themes/lara";
import { providePrimeNG } from "primeng/config";
import { AppComponent } from "./app.component";
import { PageBodyComponent } from "./layout/page/page-body/page-body.component";
import { PageHeaderComponent } from "./layout/page/page-header/page-header.component";
import { PageComponent } from "./layout/page/page.component";
import { SigncoUntypedFormBuilder } from "./services/signco-untyped-form-builder.service";

export function httpLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}

/**
 * Here we pass the configuration parameters to create an MSAL instance.
 * For more info, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/configuration.md
 */
export function msalConfigFactory(configurationService: ConfigurationService): IPublicClientApplication {
    return new PublicClientApplication({
        auth: {
            clientId: configurationService.configuration.azureAuthenticationClientId,
            authority: configurationService.configuration.azureAuthenticationAuthority,
            redirectUri: window.location.origin,
            knownAuthorities: [configurationService.configuration.azureAuthenticationAuthority],
            postLogoutRedirectUri: window.location.origin,
        },
        cache: {
            cacheLocation: BrowserCacheLocation.LocalStorage,
        },
        system: {
            /**
             * Below you can configure MSAL.js logs. For more information, visit:
             * https://docs.microsoft.com/azure/active-directory/develop/msal-logging-js
             */
            loggerOptions: {
                loggerCallback(logLevel: LogLevel, message: string) {
                    console.log(message);
                },
                logLevel: LogLevel.Warning,
                piiLoggingEnabled: false,
            },
        },
    });
}

@NgModule({
    declarations: [AppComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        SharedModule,
        Angulartics2Module.forRoot(),
        ToastrModule.forRoot(),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: httpLoaderFactory,
                deps: [HttpClient],
            },
        }),
        PageComponent,
        PageHeaderComponent,
        PageBodyComponent,
        ServiceWorkerModule.register("ngsw-worker.js", {
            enabled: !location.hostname.includes("localhost"),
            // Register the ServiceWorker as soon as the application is stable
            // or after 30 seconds (whichever comes first).
            registrationStrategy: "registerWhenStable:30000",
        }),
        CookieNotificationComponent,
    ],
    providers: [
        provideZoneChangeDetection({ eventCoalescing: true }),
        {
            provide: APP_CONFIGURATION,
            useValue: {
                svgVersion: environment.svgVersion,
                appName: "portal",
            } as AppConfiguration,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (configService: ConfigurationService, domainDataService: DomainDataService) => async () => {
                // This is executed before the application starts. This means that the rest of the code can rely on
                // the configuration and the domain data being loaded.
                await AppComponent.attemptStartup(configService, domainDataService);
            },
            deps: [ConfigurationService, DomainDataService],
            multi: true,
        },
        AdminGuard,
        AuthGuard,
        AuthorizationGuard,
        PendingChangesGuard,
        [
            {
                provide: ErrorHandler,
                useClass: SigncoErrorHandler,
            },
        ],
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: DefaultInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: JsonInterceptor,
            multi: true,
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: msalConfigFactory,
            deps: [ConfigurationService],
        },
        MsalService,
        MsalBroadcastService,
        { provide: UntypedFormBuilder, useClass: SigncoUntypedFormBuilder },
        providePrimeNG({
            theme: {
                preset: Lara,
                options: {
                    darkModeSelector: "none",
                    cssLayer: {
                        name: "primeng",
                        order: "app-styles, reset, primeng, theme",
                    },
                },
            },
        }),
    ],
    exports: [],
    bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {}
